import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { HelpCircle, Phone, MessageCircle, ChevronDown, Search, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_NUM = "7410121121";
const WHATSAPP_LINK = `https://wa.me/91${PHONE_NUM}?text=Hi,%20I%20have%20a%20question%20about%20LIC%20plans`;

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data.faqs);
      } catch (error) {
        console.error("Failed to load FAQs:", error);
        // Fallback FAQs
        setFaqs([
          {
            question: "Is LIC good for long-term saving?",
            answer: "LIC has been India's most trusted life insurance provider for over 65 years. For disciplined long-term savers who value capital protection over aggressive returns, LIC's traditional plans offer a reliable way to build wealth while ensuring life coverage."
          },
          {
            question: "Why shouldn't I focus only on returns?",
            answer: "Insurance is fundamentally about protection and disciplined saving, not maximizing returns. Traditional insurance plans provide a balanced approach - guaranteed life cover, forced savings discipline, tax benefits, and reasonable growth."
          },
          {
            question: "Is ₹5,000/month enough to start?",
            answer: "Yes, ₹5,000 per month is an excellent starting point. Over 20-25 years, this disciplined saving can accumulate to a substantial corpus. The key is starting early and staying consistent."
          },
          {
            question: "How is FutureSanchay different from regular LIC agents?",
            answer: "We are a Policy Review Platform first. Before selling anything, we analyze your existing policies and give you honest advice - even if it means recommending you NOT to buy anything new. We focus on your financial health, not our commissions."
          },
          {
            question: "Will you spam me with calls after I contact you?",
            answer: "Absolutely not. We have a strict zero-spam policy. You will receive exactly what you requested - a policy review or consultation. No repeated follow-ups, no pressure tactics. Our 4.9 Google rating reflects this commitment."
          },
          {
            question: "What if my existing policy is not good for me?",
            answer: "We'll tell you honestly. Depending on your situation, we may recommend: continuing the policy, converting it to a paid-up status, surrendering it, or sometimes even switching to a more suitable plan. The decision is always yours."
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <div className="min-h-screen bg-white page-transition" data-testid="faq-page">
      <Helmet>
        <title>LIC Policy FAQs | Common Questions Answered | FutureSanchay</title>
        <meta name="description" content="Frequently asked questions about LIC policies, surrender value, returns, and policy review. Get answers from IRDAI certified experts at FutureSanchay." />
        <link rel="canonical" href="https://futuresanchay.com/faq" />
      </Helmet>
      {/* Hero Header */}
      <section className="pt-8 pb-12 md:pt-12 md:pb-16 relative overflow-hidden" data-testid="faq-hero">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-sky-50 rounded-full blur-3xl animate-pulse opacity-60"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-emerald-50 rounded-full blur-3xl animate-pulse opacity-50" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container-main relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 animate-fade-in-down">
            <Link to="/" className="hover:text-sky-500 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">FAQs</span>
          </div>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-600 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-fade-in-up">
              <HelpCircle className="w-4 h-4" />
              Common Questions
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 animate-fade-in-up delay-100">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 animate-fade-in-up delay-200">
              Get answers to common questions about LIC plans, policy reviews, and working with FutureSanchay.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pb-12 md:pb-16">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-100 rounded w-full"></div>
                  </div>
                ))}
              </div>
            ) : (
              <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 animate-fade-in-up overflow-hidden"
                    style={{ animationDelay: `${index * 100}ms` }}
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left font-bold text-gray-900 hover:text-sky-500 hover:no-underline px-6 py-5 text-base">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 px-6 pb-5 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="pb-16 md:pb-24">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-sky-50 to-white rounded-3xl border border-sky-100 p-8 md:p-10 text-center shadow-lg animate-fade-in-up">
              <div className="w-20 h-20 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-float">
                <HelpCircle className="w-10 h-10 text-sky-500" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Still Have Questions?
              </h2>
              <p className="text-gray-600 mb-8 max-w-lg mx-auto">
                Our IRDAI certified advisors are happy to answer any questions you have about LIC plans or 
                your existing policies. Reach out to us directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
                  <Button className="h-14 px-8 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all hover:scale-105 animate-whatsapp-pulse" data-testid="faq-whatsapp-btn">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Ask on WhatsApp
                  </Button>
                </a>
                <a href={`tel:${PHONE_NUM}`}>
                  <Button variant="outline" className="h-14 px-8 rounded-xl font-bold border-2 border-sky-200 text-sky-600 hover:bg-sky-50 hover:border-sky-300 transition-all" data-testid="faq-call-btn">
                    <Phone className="w-5 h-5 mr-2" />
                    Call {PHONE_NUM}
                  </Button>
                </a>
              </div>
              <p className="text-xs text-gray-500 mt-6 flex items-center justify-center gap-1">
                <Shield className="w-4 h-4" />
                Zero spam guaranteed. Your query, your privacy.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
